import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';

/**
 * PersonasTab — CRUD for test personas per practice.
 * Add 1 or bulk-import CSV. Used by live-mode batch runner for identity binding.
 */
function PersonasTab({ practiceId }) {
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [csvText, setCsvText] = useState('');
  const [showCsv, setShowCsv] = useState(false);
  const [form, setForm] = useState({
    name: '', date_of_birth: '', phone: '', email: '', insurance: '',
    is_existing_patient: true, athena_patient_id: '', notes: '', language: 'en',
  });
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!practiceId) return;
    setLoading(true);
    try {
      const res = await api.request(`/practices/${practiceId}/voice-test/personas`);
      setPersonas(res.personas || []);
    } catch (err) {
      setError('Failed to load personas: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [practiceId]);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => setForm({
    name: '', date_of_birth: '', phone: '', email: '', insurance: '',
    is_existing_patient: true, athena_patient_id: '', notes: '', language: 'en',
  });

  const save = async () => {
    setError('');
    if (!form.name || !form.date_of_birth) { setError('name and date_of_birth required'); return; }
    try {
      if (editingId) {
        await api.request(`/practices/${practiceId}/voice-test/personas/${editingId}`, { method: 'PUT', body: form });
      } else {
        await api.request(`/practices/${practiceId}/voice-test/personas`, { method: 'POST', body: form });
      }
      resetForm();
      setEditingId(null);
      setShowAdd(false);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const startEdit = (p) => {
    setForm({
      name: p.name, date_of_birth: p.date_of_birth?.slice ? p.date_of_birth.slice(0, 10) : p.date_of_birth,
      phone: p.phone || '', email: p.email || '', insurance: p.insurance || '',
      is_existing_patient: p.is_existing_patient, athena_patient_id: p.athena_patient_id || '',
      notes: p.notes || '', language: p.language || 'en',
    });
    setEditingId(p.id);
    setShowAdd(true);
  };

  const del = async (id) => {
    if (!window.confirm('Delete this persona?')) return;
    try {
      await api.request(`/practices/${practiceId}/voice-test/personas/${id}`, { method: 'DELETE' });
      await load();
    } catch (err) {
      setError('Delete failed: ' + err.message);
    }
  };

  const importCsv = async () => {
    setError('');
    // Expected CSV columns: name,date_of_birth,phone,email,insurance,is_existing_patient,notes,language
    const rows = csvText.trim().split(/\r?\n/);
    if (rows.length < 2) { setError('CSV must have a header + at least one row'); return; }
    const header = rows[0].split(',').map(h => h.trim().toLowerCase());
    const personas = rows.slice(1).map(r => {
      const cols = r.split(',');
      const obj = {};
      header.forEach((h, i) => { obj[h] = cols[i]?.trim(); });
      if (obj.is_existing_patient !== undefined) obj.is_existing_patient = obj.is_existing_patient === 'true';
      return obj;
    }).filter(p => p.name && p.date_of_birth);
    if (!personas.length) { setError('No valid rows found — check "name" and "date_of_birth" columns'); return; }
    try {
      const res = await api.request(`/practices/${practiceId}/voice-test/personas/bulk`, { method: 'POST', body: { personas } });
      setCsvText('');
      setShowCsv(false);
      await load();
      alert(`Imported ${res.created} personas`);
    } catch (err) {
      setError('Bulk import failed: ' + err.message);
    }
  };

  return (
    <div style={S.container}>
      {error && <div style={S.err}>{error}</div>}

      <div style={S.headerRow}>
        <h3 style={S.h3}>{personas.length} persona{personas.length === 1 ? '' : 's'}</h3>
        <div>
          <button style={S.btnPrimary} onClick={() => { resetForm(); setEditingId(null); setShowAdd(true); }}>+ Add Persona</button>
          <button style={S.btnSecondary} onClick={() => setShowCsv(!showCsv)}>Import CSV</button>
        </div>
      </div>

      {showCsv && (
        <div style={S.card}>
          <p style={{ marginTop: 0 }}>Paste CSV with header row. Columns: <code>name,date_of_birth,phone,email,insurance,is_existing_patient,notes,language</code></p>
          <textarea style={S.csvArea} value={csvText} onChange={e => setCsvText(e.target.value)} placeholder="name,date_of_birth,phone,email,insurance,is_existing_patient,notes,language&#10;Priya Menon,1988-03-22,6023198745,,,true,Calm existing patient,en" />
          <div style={{ marginTop: 8 }}>
            <button style={S.btnPrimary} onClick={importCsv}>Import</button>
            <button style={S.btnSecondary} onClick={() => setShowCsv(false)}>Cancel</button>
          </div>
        </div>
      )}

      {showAdd && (
        <div style={S.card}>
          <h4 style={{ marginTop: 0 }}>{editingId ? 'Edit persona' : 'New persona'}</h4>
          <div style={S.formGrid}>
            <label>Name<input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></label>
            <label>DOB<input type="date" value={form.date_of_birth} onChange={e => setForm({ ...form, date_of_birth: e.target.value })} /></label>
            <label>Phone<input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></label>
            <label>Email<input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></label>
            <label>Insurance<input value={form.insurance} onChange={e => setForm({ ...form, insurance: e.target.value })} placeholder="BCBS" /></label>
            <label>Language<select value={form.language} onChange={e => setForm({ ...form, language: e.target.value })}><option value="en">English</option><option value="es">Spanish</option></select></label>
            <label>Existing patient?<select value={form.is_existing_patient ? 'yes' : 'no'} onChange={e => setForm({ ...form, is_existing_patient: e.target.value === 'yes' })}><option value="yes">Yes</option><option value="no">No (new patient)</option></select></label>
            <label>Athena Patient ID<input value={form.athena_patient_id} onChange={e => setForm({ ...form, athena_patient_id: e.target.value })} placeholder="(for live mode)" /></label>
          </div>
          <label style={{ display: 'block', marginTop: 12 }}>Notes / personality
            <textarea rows="2" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Calm existing patient, quick to end call if info received" />
          </label>
          <div style={{ marginTop: 12 }}>
            <button style={S.btnPrimary} onClick={save}>{editingId ? 'Save' : 'Create'}</button>
            <button style={S.btnSecondary} onClick={() => { setShowAdd(false); setEditingId(null); }}>Cancel</button>
          </div>
        </div>
      )}

      {loading && <p>Loading...</p>}
      {!loading && personas.length === 0 && <p style={S.empty}>No personas yet. Add one to bind identities for live-mode runs.</p>}
      {personas.length > 0 && (
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Name</th>
              <th style={S.th}>DOB</th>
              <th style={S.th}>Phone</th>
              <th style={S.th}>Insurance</th>
              <th style={S.th}>Existing?</th>
              <th style={S.th}>Language</th>
              <th style={S.th}>Notes</th>
              <th style={S.th}></th>
            </tr>
          </thead>
          <tbody>
            {personas.map(p => (
              <tr key={p.id}>
                <td style={S.td}><b>{p.name}</b></td>
                <td style={S.td}>{typeof p.date_of_birth === 'string' ? p.date_of_birth.slice(0, 10) : String(p.date_of_birth).slice(0, 10)}</td>
                <td style={S.td}>{p.phone}</td>
                <td style={S.td}>{p.insurance}</td>
                <td style={S.td}>{p.is_existing_patient ? 'Yes' : 'New'}</td>
                <td style={S.td}>{p.language}</td>
                <td style={S.td} title={p.notes}>{(p.notes || '').slice(0, 40)}</td>
                <td style={S.td}>
                  <button style={S.btnSmall} onClick={() => startEdit(p)}>Edit</button>
                  <button style={S.btnSmallDanger} onClick={() => del(p.id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

const S = {
  container: { display: 'flex', flexDirection: 'column', gap: 12 },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  h3: { margin: 0, color: '#111827', fontSize: 18 },
  card: { background: 'white', padding: 16, borderRadius: 8, border: '1px solid #e5e7eb' },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 },
  csvArea: { width: '100%', minHeight: 160, fontFamily: 'monospace', fontSize: 12, padding: 8, border: '1px solid #d1d5db', borderRadius: 4 },
  table: { width: '100%', borderCollapse: 'collapse', background: 'white', borderRadius: 4, overflow: 'hidden' },
  th: { padding: '8px 12px', textAlign: 'left', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', fontSize: 13, fontWeight: 600 },
  td: { padding: '8px 12px', borderBottom: '1px solid #f3f4f6', fontSize: 13 },
  btnPrimary: { background: '#2563eb', color: 'white', border: 'none', padding: '8px 16px', borderRadius: 4, cursor: 'pointer', marginRight: 8 },
  btnSecondary: { background: '#e5e7eb', color: '#111827', border: 'none', padding: '8px 16px', borderRadius: 4, cursor: 'pointer', marginRight: 8 },
  btnSmall: { background: '#e5e7eb', color: '#111827', border: 'none', padding: '4px 10px', borderRadius: 4, cursor: 'pointer', marginRight: 4, fontSize: 12 },
  btnSmallDanger: { background: '#fee2e2', color: '#991b1b', border: 'none', padding: '4px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 12 },
  empty: { color: '#6b7280', fontStyle: 'italic', padding: 20, textAlign: 'center' },
  err: { background: '#fee2e2', color: '#991b1b', padding: 10, borderRadius: 4, fontSize: 14 },
};

export default PersonasTab;
